<template>
  <div
    v-if="!loading"
    class="custom-chart-container"
    :style="`background-color: ${isValidRawData ? backgroundColor : DEFAULT_BACKGROUND_COLOR}`"
    ref="customChartContainerRef"
  >
    <div class="custom-chart-title-container">
      <div class="left d-flex align-items-center" style="width: 40%">
        <div class="d-flex">
          <div class="custom-chart-title-label" :style="`color: ${isValidRawData ? textTitleColor : DEFAULT_TEXT_TITLE_COLOR}`">
            {{ chartProps.title }}
          </div>
          <div class="d-inline-block" v-bt-tooltips :title="chartProps.tooltip">
            <title-question-icon class="chart-title-question-icon" :style="`fill: ${isValidRawData ? iconColor : DEFAULT_ICON_COLOR}`" />
          </div>
        </div>
      </div>
      <div class="right d-flex justify-content-end align-items-center">
        <div v-if="isFilterChurn" class="d-flex align-items-center gap-2">
          <div>แนวโน้มการสูญเสียลูกค้าแบบภาพรวม</div>
          <div>
            <button @click="onClickCalButton" class="standard-formula">
              <SettingsButtonIcon /> <span>{{ false ? 'Adjusted' : 'Standard' }} Formula</span>
            </button>
          </div>
        </div>
        <charts-action-group-button
          :icon-color="isValidRawData ? iconColor : DEFAULT_ICON_COLOR"
          @on-click-data-info="$emit('on-click-data-info', chartLayoutId, chartProps, startDate, endDate)"
          @on-click-setting-chart="$emit('on-click-setting-chart', chartLayoutId, chartProps, startDate, endDate)"
          @on-click-delete="$emit('on-click-delete', chartLayoutId, chartProps, startDate, endDate)"
          :loading-data-info="loadingDataInfo"
          :loading-setting-chart="loadingSettingChart"
          :loading-delete="loadingDelete"
        />
      </div>
    </div>
    <div class="custom-chart-filter-container d-flex align-items-center" style="margin-left: -3px; margin-top: 10px; gap: 20px">
      <div class="date-picker">
        <choco-date-range-picker
          v-if="chartProps.rawData.filter.dateTime"
          v-model:start="startDate"
          v-model:end="endDate"
          format-separator="to"
          v-model:relative-model="relativeModel"
          :relative-options="['today', 'P7D', 'P15D', 'P30D', 'P90D']"
          :format="'dd MMM yyyy'"
          @click="onClickElemInChart"
          @hide="onOutClickElemInChart"
          :is-show-apply-button="true"
          class="mt-1"
        >
          <template v-slot:input="props">
            <date-range-display-input
              v-bind="props"
              size="small"
              prepend-icon
              minimal
              graph-style
              style="max-width: 280px"
              :color-style="datePickerColor"
              :style="`color: ${isValidRawData ? datePickerColor : DEFAULT_DATE_PICKER_COLOR}`"
            >
              <template #prepend>
                <datetime-icon class="date-time-icon" :style="`fill: ${isValidRawData ? datePickerColor : DEFAULT_DATE_PICKER_COLOR}`" />
              </template>
              <template #append>
                <div style="padding: 0 8px; transform: rotate(-90deg)">
                  <arrow-down-icon :style="`fill: ${isValidRawData ? datePickerColor : DEFAULT_DATE_PICKER_COLOR}`" />
                </div>
              </template>
            </date-range-display-input>
          </template>
        </choco-date-range-picker>
        <div class="custom-chart-no-filter" v-else>
          <datetime-no-filter-icon :style="`fill: ${isValidRawData ? datePickerColor : DEFAULT_DATE_PICKER_COLOR}`" />
          <div :style="`color: ${isValidRawData ? datePickerColor : DEFAULT_DATE_PICKER_COLOR}`">{{ t('bi.no_filter') }}</div>
        </div>
      </div>
      <div v-if="isFilterSource" class="ml-2">
        <source-filter-dropdown
          v-model="filterChartSource"
          :optionList="props.filterChartSourceOptions"
          @click="onClickElemInChart"
          @hide="onOutClickElemInChart"
          @reset="onOutClickElemInChart"
        />
      </div>
      <div v-if="isFilterSegment">
        <segment-filter-dropdown
          v-model="filterChartSegment"
          :optionList="props.filterChartSegmentOptions"
          @click="onClickElemInChart"
          @hide="onOutClickElemInChart"
          @reset="onOutClickElemInChart"
        />
      </div>
      <div v-if="isFilterStage" class="d-flex align-items-center stage">
        <div class="title">Stage</div>
        <div>
          <v-select
            :modelValue="churnRateStage"
            @update:modelValue="setChurnRateStage"
            :options="dropDownStage"
            :clearable="false"
            :searchable="false"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><down-ward-icon fill="#007fff" /></span>
            </template>
          </v-select>
        </div>
      </div>
      <div v-else-if="isFilterStageAll" class="d-flex align-items-center stage">
        <div class="title">Stage</div>
        <div>
          <v-select
            :modelValue="churnRateStageAll"
            @update:modelValue="setChurnRateStageAll"
            :options="dropDownStageAll"
            :clearable="false"
            :searchable="false"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><down-ward-icon fill="#007fff" /></span>
            </template>
          </v-select>
        </div>
      </div>
    </div>
    <div v-if="isFilterSource && filterChartSource.length" class="custom-chart-filter-container branch-selection">
      <div class="dynamic-filter-block">
        <div class="dynamic-filter-wrp">
          <div v-for="(item, index) in filterChartSource" :key="index" class="dynamic-item" @click="onClickSeeMoreFilter">
            {{ item }}
          </div>
        </div>
      </div>
      <div class="custom-filter-see-more-wrp">
        <div class="see-more-btn" @click="onClickSeeMoreFilter"><see-more-icon /></div>
      </div>
    </div>
    <div class="custom-charts-wrp">
      <template v-if="isValidRawData">
        <slot name="top">
          <component :is="resolveExtraTopComponent()" v-bind="resolveExtraTopProps()" />
        </slot>
        <div class="d-flex justify-content-center h-100 px-3">
          <slot name="left">
            <component :is="resolveExtraLeftComponent()" v-bind="resolveExtraLeftProps()" />
          </slot>
          <slot>
            <component :is="resolveByChartType()" v-bind="resolveProps()" v-on="resolveEmits()" />
          </slot>
          <slot name="right">
            <component :is="resolveExtraRightComponent()" v-bind="resolveExtraRightProps()" />
          </slot>
        </div>
        <slot name="bottom"></slot>
      </template>
      <div v-else class="empty-chart-icon-container">
        <empty-chart-icon />
      </div>
    </div>
    <CalculateModal
      :is-standard="isSelectStandard"
      :is-open="isOpenCalModal"
      :loading="false"
      @close-modal="onCloseCalModal"
      @apply-modal="onClickApply"
    />
  </div>
  <div v-else class="d-flex justify-content-center align-items-center">
    <loader-spinner />
  </div>
</template>

<script setup lang="ts">
//vue
import { ref, Ref } from 'vue';

//libs
import { useI18n } from 'vue-i18n';

//components
import { ChocoDateRangePicker } from '@choco-platform/components';
import DateRangeDisplayInput from '@/views/components/date-display-input/DateRangeDisplayInput.vue';
import LoaderSpinner from '@/views/components/loader/loader-spinner.vue';
import ChartsActionGroupButton from '@/views/modules/analytics/components/bi-chart/ChartsActionGroupButton.vue';
import SourceFilterDropdown from '@/views/modules/analytics/components/anal-churnrate/chart-churn-rate/chart-container-filter/SourceFilterDropdown.vue';
import SegmentFilterDropdown from '@/views/modules/analytics/components/anal-churnrate/chart-churn-rate/chart-container-filter/SegmentFilterDropdown.vue';
import CalculateModal from '../CalculateModal.vue';
import useValidationModal from '@/views/components/modal/hooks/useValidationModal';

//icon
import TitleQuestionIcon from '@assets/icons/modules/analytics/bi/TitleQuestionIcon.vue';
import DatetimeNoFilterIcon from '@assets/icons/base/datetime-picker/DatetimeNoFilterIcon.vue';
import ArrowDownIcon from '@assets/icons/base/navbarIcon/ArrowDownIcon.vue';
import EmptyChartIcon from '@assets/icons/modules/analytics/bi/EmptyChartIcon.vue';
import DatetimeIcon from '@assets/icons/base/datetime-picker/DatetimeIcon.vue';
import DownWardIcon from '@/assets/icons/base/v-select/DownWardIcon.vue';
import SettingsButtonIcon from '@assets/icons/modules/analytics/churn-rate/SettingsButtonIcon.vue';
import SeeMoreIcon from '@assets/icons/modules/analytics/bi/SeeMoreIcon.vue';

//hooks
import { useElementSize } from '@vueuse/core';
import useAnalyticsChartContainer from '../hooks/useChartChurnRateContainer';

interface Props {
  chartLayoutId: number;
  width: number;
  height: number;
  index: string;
  sourceList: { label: string; value: string }[];
  filterChartSourceOptions: Bi.Filter.DynamicOptions | null;
  filterChartSegmentOptions: Bi.Filter.DynamicOptions | null;
  mainStartDate: Date | null;
  mainEndDate: Date | null;
  mainRelativeModel: string | null;
  isMainFilterChange: boolean;
  loadingDataInfo: boolean;
  loadingSettingChart: boolean;
  loadingDelete: boolean;
  filterList: any[];
}

const props = withDefaults(defineProps<Props>(), {
  loadingDataInfo: false,
  loadingSettingChart: false,
  loadingDelete: false,
  useDynamicFilter: false,
});

defineEmits(['on-click-data-info', 'on-click-setting-chart', 'on-click-delete', 'on-fetched', 'see-more-filter']);

export interface SourceListOptions {
  label: string;
  value: string;
}

interface ChurnRateStage {
  id: number;
  label: string;
  ids: number[];
}

const { t } = useI18n();
const customChartContainerRef = ref(null);
const { width: componentWidth, height: componentHeight } = useElementSize(customChartContainerRef);

const { openSuccessModal } = useValidationModal();

const churnRateStage: Ref<ChurnRateStage> = ref({ id: 1, label: t('Normal'), ids: [] as number[] });
const churnRateStageAll: Ref<ChurnRateStage> = ref({ id: 0, label: t('All Stage'), ids: [] as number[] });

const isSelectStandard: Ref<boolean> = ref(true);
const isOpenCalModal: Ref<boolean> = ref(false);

const setChurnRateStage = (stage: { id: number; label: string; ids: number[] }) => {
  churnRateStage.value = stage;

  onClickElemInChart();
  onOutClickElemInChart();
};

const setChurnRateStageAll = (stage: { id: number; label: string; ids: number[] }) => {
  churnRateStageAll.value = stage;

  onClickElemInChart();
  onOutClickElemInChart();
};

function onClickCalButton() {
  isOpenCalModal.value = true;
}

function onClickApply(isStandard: boolean) {
  isSelectStandard.value = isStandard;
  openSuccessModal('ทำรายการสำเร็จ', '', '', undefined, () => onCloseCalModal());
}

function onCloseCalModal() {
  isOpenCalModal.value = false;
}

const {
  DEFAULT_TEXT_TITLE_COLOR,
  DEFAULT_ICON_COLOR,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_DATE_PICKER_COLOR,
  textTitleColor,
  iconColor,
  datePickerColor,
  backgroundColor,
  isValidRawData,
  chartProps,
  startDate,
  endDate,
  relativeModel,
  filterChartSource,
  filterChartSegment,
  loading,
  fetchOptionGraphById,
  onOutClickElemInChart,
  onClickElemInChart,
  resolveByChartType,
  resolveProps,
  resolveExtraTopComponent,
  resolveExtraTopProps,
  resolveExtraRightComponent,
  resolveExtraRightProps,
  resolveExtraLeftComponent,
  resolveExtraLeftProps,
  resolveEmits,
  isFilterSource,
  isFilterSegment,
  isFilterStage,
  isFilterStageAll,
  isFilterChurn,
  onClickSeeMoreFilter,
  dropDownStage,
  dropDownStageAll,
} = useAnalyticsChartContainer(props, componentWidth, componentHeight, churnRateStage, churnRateStageAll);

defineExpose({ fetchOptionGraphById, filterChartSource });
</script>

<style lang="scss" scoped>
@import '@assets/styles/modules/analytics/churn-rate/chart/custom-chart-container.scss';

.dynamic-filter-wrp {
  background-color: #e5e9ef;
  width: 100%;
  height: 35px;
  border-radius: 0.5rem;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 4px;
  font-size: 15px;

  gap: 0.5rem;
  overflow: hidden;
  overflow-x: auto;
  white-space: nowrap;
  z-index: 100;
  /* Auto hide scrollbar */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    height: 0.3rem;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0); /* Transparent background */
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.2); /* Show on hover */
  }
  .dynamic-item {
    background-color: #3191ff;
    color: #ffffff;
    font-weight: 400;
    height: 100%;
    white-space: nowrap;
    border-radius: 0.5rem;
    padding: 4px 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    &.active {
      background-color: #012f64;
    }
  }
}

.dynamic-filter-block {
  width: calc(100% - 40px); // 40px = see more button

  .dynamic-filter-wrp {
    padding: 4px;
    width: 100%;
    height: 35px;
    position: relative;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: 15px;
    gap: 0.5rem;
    overflow: hidden;
    overflow-x: auto;
    white-space: nowrap;
    z-index: 100;
    /* Auto hide scrollbar */
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
      height: 0.3rem;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0); /* Transparent background */
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: rgba(0, 0, 0, 0.2); /* Show on hover */
    }
    .dynamic-item {
      background-color: #3191ff;
      color: #ffffff;
      font-weight: 400;
      height: 100%;
      white-space: nowrap;
      border-radius: 0.5rem;
      padding: 4px 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      &.active {
        background-color: #012f64;
      }
    }
  }
}

:deep(.standard-formula) {
  max-width: 235px;
  padding: 8px 16px 8px 16px;
  gap: 8px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #007fff;
  cursor: pointer;
  path {
    stroke: #007fff;
  }
  span {
    color: #007fff;
  }
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }
}

.custom-chart-filter-container {
  display: flex;
  // height: auto;
  margin-bottom: 10px;
  height: 30px;
  width: 100%;

  &.branch-selection {
    background-color: #e5e9ef;
    padding: 4px;
    border-radius: 0.5rem;
    height: auto;
  }
  .custom-chart-no-filter {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #bebebe;

    div {
      padding-left: 4px;
    }

    svg {
      vertical-align: sub;
      width: 20px;
      fill: #bebebe;
    }
  }

  .custom-filter-see-more-wrp {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;

    .see-more-btn {
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 10px;
      border-radius: 0.5rem;
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.25);
      }

      svg {
        object-fit: contain;
        width: 16px;
        height: 100%;
      }
    }
  }
}
</style>
